import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Alert, Result, Spin, Typography, Table, Badge, Tag, Modal, Tabs, message, Space, Divider, Descriptions } from 'antd';
import { KeyOutlined, DesktopOutlined, CheckCircleOutlined, UserOutlined, LogoutOutlined, LockOutlined, SafetyOutlined, HistoryOutlined, SettingOutlined, EditOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

// API 基础路径 - 自动适配部署路径（支持子目录如 /license/）
const getBasePath = () => {
  const path = window.location.pathname;
  // 统一使用 /api
  return '/api';
};
const API_BASE_URL = getBasePath();

const Activate = () => {
  const { customerLogin, logout, user, userType, getAuthHeaders, loading: authLoading, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [loginForm] = Form.useForm();
  const [activateForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [profileForm] = Form.useForm();
  
  // 客户数据
  const [mySerials, setMySerials] = useState([]);
  const [myFingerprints, setMyFingerprints] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  
  // 模态框
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // 如果已登录，获取客户数据
  useEffect(() => {
    if (user && userType === 'customer') {
      fetchMyData();
      // 初始化表单值
      profileForm.setFieldsValue({
        username: user?.username,
        company_name: user?.company_name,
        contact_person: user?.contact_person,
        email: user?.email,
        phone: user?.phone
      });
    }
  }, [user, userType]);

  const fetchMyData = async () => {
    setDataLoading(true);
    try {
      // 并行获取序列号、硬件指纹和统计信息
      const [serialsRes, fingerprintsRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/my/serials`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE_URL}/my/fingerprints`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE_URL}/my/stats`, { headers: getAuthHeaders() })
      ]);
      
      if (serialsRes.data.success) {
        setMySerials(serialsRes.data.data);
      }
      if (fingerprintsRes.data.success) {
        setMyFingerprints(fingerprintsRes.data.data);
      }
      if (statsRes.data.success) {
        setMyStats(statsRes.data.data);
      }
    } catch (error) {
      console.error('获取数据失败:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleLogin = async (values) => {
    setLoading(true);
    setError('');
    
    const result = await customerLogin(values.username, values.password);
    
    if (!result.success) {
      setError(result.message);
    }
    
    setLoading(false);
  };

  const handleLogout = () => {
    logout();
    setMySerials([]);
    setMyFingerprints([]);
    setMyStats(null);
    setSuccess(null);
    loginForm.resetFields();
  };

  const onFinish = async (values) => {
    setLoading(true);
    setError('');
    setSuccess(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/activate`, {
        serial_number: values.serial_number.trim(),
        hardware_fingerprint: values.hardware_fingerprint.trim()
      });

      if (response.data.success) {
        setSuccess(response.data.data);
        // 刷新数据
        fetchMyData();
      }
    } catch (error) {
      setError(error.response?.data?.message || '激活失败，请检查输入信息');
    } finally {
      setLoading(false);
    }
  };

  // 修改账户信息
  const handleUpdateProfile = async (values) => {
    // 前端验证：联系人和电话至少填一个（建议但不强制）
    if (!values.contact_person?.trim() && !values.phone?.trim()) {
      message.warning('建议填写联系人或电话，方便管理员联系您');
      // 不阻止提交，只是提示
    }
    
    setProfileLoading(true);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/my/profile`,
        {
          username: values.username?.trim(),
          company_name: values.company_name?.trim(),
          contact_person: values.contact_person?.trim(),
          email: values.email?.trim(),
          phone: values.phone?.trim()
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        message.success('账户信息更新成功');
        // 更新本地用户信息
        setUser(response.data.data);
        setProfileModalVisible(false);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message;
      if (errorMsg?.includes('用户名') || errorMsg?.includes('已被使用')) {
        message.error('该用户名已被其他用户使用，请更换');
      } else if (errorMsg?.includes('不能为空')) {
        message.error(errorMsg);
      } else {
        message.error(errorMsg || '账户信息更新失败，请检查网络连接');
      }
    } finally {
      setProfileLoading(false);
    }
  };

  // 修改密码
  const handleChangePassword = async (values) => {
    setPasswordLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/my/change-password`,
        {
          old_password: values.old_password,
          new_password: values.new_password
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        message.success('密码修改成功');
        setPasswordModalVisible(false);
        passwordForm.resetFields();
      }
    } catch (error) {
      message.error(error.response?.data?.message || '密码修改失败');
    } finally {
      setPasswordLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('已复制到剪贴板');
  };

  // 序列号表格列定义
  const serialColumns = [
    {
      title: '序列号',
      dataIndex: 'serial_number',
      key: 'serial_number',
      render: (text) => <code>{text}</code>
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Badge status={isActive ? 'success' : 'error'} text={isActive ? '有效' : '禁用'} />
      )
    },
    {
      title: '激活情况',
      key: 'activations',
      render: (_, record) => (
        <span>{record.current_activations} / {record.max_activations}</span>
      )
    },
    {
      title: '剩余次数',
      dataIndex: 'remaining_activations',
      key: 'remaining_activations',
      render: (num) => <Tag color={num > 0 ? 'green' : 'red'}>{num}</Tag>
    },
    {
      title: '过期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD') : '永不过期'
    },
    {
      title: '备注',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      render: (text) => text || '-'
    }
  ];

  // 硬件指纹表格列定义
  const fingerprintColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70
    },
    {
      title: '硬件指纹',
      dataIndex: 'hardware_fingerprint',
      key: 'hardware_fingerprint',
      ellipsis: true,
      render: (text) => <code style={{ fontSize: 11 }}>{text}</code>
    },
    {
      title: '许可证码',
      dataIndex: 'license_code',
      key: 'license_code',
      ellipsis: true,
      render: (text) => <code style={{ fontSize: 10 }}>{text?.substring(0, 30)}...</code>
    },
    {
      title: '激活时间',
      dataIndex: 'activated_at',
      key: 'activated_at',
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '-',
      width: 150
    },
    {
      title: '最后验证',
      dataIndex: 'last_verified_at',
      key: 'last_verified_at',
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '从未',
      width: 150
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => <Badge status={isActive ? 'success' : 'default'} text={isActive ? '有效' : '禁用'} />,
      width: 80
    }
  ];

  // 如果正在加载认证状态
  if (authLoading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        background: '#f0f2f5'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // 如果未登录，显示登录界面
  if (!user || userType !== 'customer') {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 24
      }}>
        <Card style={{ maxWidth: 400, width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <Title level={3}>客户登录</Title>
            <Text type="secondary">请先登录以查看您的序列号和激活软件</Text>
          </div>

          {error && (
            <Alert 
              message={error} 
              type="error" 
              showIcon 
              style={{ marginBottom: 16 }} 
            />
          )}

          <Form
            name="login"
            form={loginForm}
            onFinish={handleLogin}
            autoComplete="off"
            layout="vertical"
          >
            <Form.Item
              name="username"
              rules={[{ required: true, message: '请输入用户名' }]}
            >
              <Input 
                prefix={<UserOutlined />} 
                placeholder="用户名" 
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: '请输入密码' }]}
            >
              <Input.Password 
                prefix={<LockOutlined />}
                placeholder="密码" 
                size="large"
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" size="large" block loading={loading}>
                登录
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              请到 <a href="http://trioop.com/download/trioop.zip" target="_blank" rel="noopener noreferrer">trioop.com/download/trioop.zip</a> 下载软件
            </Text>
          </div>
        </Card>
      </div>
    );
  }

  // 激活成功后的界面
  if (success) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        background: '#f0f2f5',
        padding: 24
      }}>
        <Card style={{ maxWidth: 600, width: '100%' }}>
          <Result
            status="success"
            title="激活成功"
            subTitle={success.is_reactivation ? '该硬件已激活过，返回现有许可证' : '请保存好您的许可证码'}
          />
          
          <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', padding: 16, borderRadius: 8, marginBottom: 16 }}>
            <Text strong>您的许可证码（点击复制）：</Text>
            <div 
              onClick={() => copyToClipboard(success.license_code)}
              style={{ 
                marginTop: 8, 
                padding: 12, 
                background: '#fff', 
                borderRadius: 4,
                fontFamily: 'monospace',
                fontSize: 12,
                wordBreak: 'break-all',
                cursor: 'pointer'
              }}
            >
              {success.license_code}
            </div>
          </div>

          {!success.is_reactivation && (
            <Alert
              message={`剩余可激活次数: ${success.remaining_activations}`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Button type="primary" block onClick={() => { setSuccess(null); activateForm.resetFields(); }}>
            继续激活其他设备
          </Button>
        </Card>
      </div>
    );
  }

  // 已登录客户的主界面
  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f0f2f5',
      padding: 24
    }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        {/* 头部 */}
        <Card style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Title level={4} style={{ margin: 0 }}>
                <UserOutlined /> 欢迎, {user?.company_name || user?.username}
              </Title>
              <Text type="secondary">客户激活中心 | 用户名: {user?.username}</Text>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  软件下载：<a href="http://trioop.com/download/trioop.zip" target="_blank" rel="noopener noreferrer">trioop.com/download/trioop.zip</a>
                </Text>
              </div>
            </div>
            <Space>
              <Button icon={<SettingOutlined />} onClick={() => setProfileModalVisible(true)}>
                账户设置
              </Button>
              <Button icon={<LogoutOutlined />} onClick={handleLogout}>
                退出登录
              </Button>
            </Space>
          </div>
        </Card>

        {dataLoading ? (
          <div style={{ textAlign: 'center', padding: 50 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Tabs defaultActiveKey="1" type="card">
            <TabPane tab={<span><KeyOutlined /> 我的序列号</span>} key="1">
              {/* 统计信息 */}
              {myStats && (
                <Card style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
                    <div>
                      <Text type="secondary">序列号数量</Text>
                      <div style={{ fontSize: 24, fontWeight: 'bold' }}>{myStats.total_serials}</div>
                    </div>
                    <div>
                      <Text type="secondary">总激活配额</Text>
                      <div style={{ fontSize: 24, fontWeight: 'bold' }}>{myStats.total_max_activations}</div>
                    </div>
                    <div>
                      <Text type="secondary">已使用</Text>
                      <div style={{ fontSize: 24, fontWeight: 'bold' }}>{myStats.total_current_activations}</div>
                    </div>
                    <div>
                      <Text type="secondary">剩余配额</Text>
                      <div style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}>
                        {myStats.remaining_activations}
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* 序列号列表 */}
              <Card title="序列号列表">
                {mySerials.length > 0 ? (
                  <Table
                    columns={serialColumns}
                    dataSource={mySerials}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    scroll={{ x: 1000 }}
                  />
                ) : (
                  <Alert message="您暂时没有分配的序列号，请联系管理员" type="warning" />
                )}
              </Card>
            </TabPane>

            <TabPane tab={<span><HistoryOutlined /> 激活记录</span>} key="2">
              {/* 硬件指纹列表 */}
              <Card title="已激活设备">
                {myFingerprints.length > 0 ? (
                  <Table
                    columns={fingerprintColumns}
                    dataSource={myFingerprints}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    size="small"
                    scroll={{ x: 1200 }}
                  />
                ) : (
                  <Alert message="暂无激活记录" type="info" />
                )}
              </Card>
            </TabPane>

            <TabPane tab={<span><DesktopOutlined /> 软件激活</span>} key="3">
              {/* 激活表单 */}
              <Card title="软件激活" style={{ maxWidth: 600, margin: '0 auto' }}>
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary">输入您的序列号和硬件指纹进行激活</Text>
                </div>

                {error && (
                  <Alert 
                    message={error} 
                    type="error" 
                    showIcon 
                    style={{ marginBottom: 16 }} 
                  />
                )}

                <Form
                  name="activate"
                  form={activateForm}
                  onFinish={onFinish}
                  autoComplete="off"
                  layout="vertical"
                >
                  <Form.Item
                    name="serial_number"
                    rules={[{ required: true, message: '请输入序列号' }]}
                  >
                    <Input 
                      prefix={<KeyOutlined />} 
                      placeholder="序列号 (格式: XXXXX-XXXXX-XXXXX-XXXXX-XXXXX)" 
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    name="hardware_fingerprint"
                    rules={[{ required: true, message: '请输入硬件指纹' }]}
                  >
                    <Input.TextArea 
                      placeholder="硬件指纹（由软件客户端提供）" 
                      size="large"
                      rows={4}
                    />
                  </Form.Item>

                  <Form.Item>
                    <Button type="primary" htmlType="submit" size="large" block loading={loading}>
                      {loading ? <Spin size="small" /> : '立即激活'}
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </TabPane>
          </Tabs>
        )}
      </div>

      {/* 账户设置模态框 */}
      <Modal
        title={<span><SettingOutlined /> 账户设置</span>}
        open={profileModalVisible}
        onCancel={() => { setProfileModalVisible(false); }}
        footer={null}
        destroyOnClose
        width={500}
      >
        <Tabs defaultActiveKey="1">
          <TabPane tab="基本信息" key="1">
            <Form
              form={profileForm}
              layout="vertical"
              onFinish={handleUpdateProfile}
              initialValues={{
                username: user?.username,
                company_name: user?.company_name,
                contact_person: user?.contact_person,
                email: user?.email,
                phone: user?.phone
              }}
            >
              <Form.Item
                name="username"
                label="用户名"
                rules={[{ required: true, message: '请输入用户名' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="登录用户名" />
              </Form.Item>

              <Form.Item
                name="company_name"
                label="公司名称"
              >
                <Input placeholder="公司名称（可选）" />
              </Form.Item>

              <Form.Item
                name="contact_person"
                label="联系人"
                extra="建议填写，方便管理员联系您"
              >
                <Input placeholder="联系人姓名" />
              </Form.Item>

              <Form.Item
                name="email"
                label="邮箱"
                rules={[{ type: 'email', message: '请输入有效的邮箱地址' }]}
              >
                <Input placeholder="邮箱（可选）" />
              </Form.Item>

              <Form.Item
                name="phone"
                label="电话"
                extra="建议填写，方便管理员联系您"
              >
                <Input placeholder="联系电话" />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" block loading={profileLoading}>
                  保存修改
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab="修改密码" key="2">
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handleChangePassword}
            >
              <Form.Item
                name="old_password"
                label="原密码"
                rules={[{ required: true, message: '请输入原密码' }]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="请输入原密码" />
              </Form.Item>

              <Form.Item
                name="new_password"
                label="新密码"
                rules={[
                  { required: true, message: '请输入新密码' },
                  { min: 6, message: '密码长度至少6位' }
                ]}
              >
                <Input.Password prefix={<SafetyOutlined />} placeholder="请输入新密码（至少6位）" />
              </Form.Item>

              <Form.Item
                name="confirm_password"
                label="确认新密码"
                rules={[
                  { required: true, message: '请确认新密码' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('new_password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的密码不一致'));
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<SafetyOutlined />} placeholder="请再次输入新密码" />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" block loading={passwordLoading}>
                  确认修改密码
                </Button>
              </Form.Item>
            </Form>
          </TabPane>
        </Tabs>
      </Modal>
    </div>
  );
};

export default Activate;
